import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { tokens } from "../../theme/tokens";
import { DuplicateMatch } from "../../hooks/NewCaseView/useDuplicateCheck";

export const VISIBLE_MATCHES = 3;

const caption = {
  fontSize: tokens.type.caption.size,
  lineHeight: tokens.type.caption.lineHeight,
} as const;

const detail = (m: DuplicateMatch) =>
  [m.client, m.subject_name, m.status, m.created_at && `created ${m.created_at}`]
    .filter(Boolean)
    .join(" · ");

/**
 * Sits beside the field label while the lookup is in flight. It fits inside
 * the label's line, so showing and hiding it never moves the form.
 */
export function DuplicateChecking({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.space[1],
        marginLeft: tokens.space[2],
        color: tokens.color.text.secondary,
        ...caption,
      }}
    >
      <CircularProgress
        aria-hidden="true"
        size={tokens.icon.sm}
        sx={{
          color: tokens.color.text.secondary,
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            "& .MuiCircularProgress-circle": { animation: "none" },
          },
        }}
      />
      Checking…
    </Box>
  );
}

interface DuplicateWarningProps {
  matches: DuplicateMatch[];
  // What matched, for the heading: "Client File #" or "property address"
  label: string;
}

/**
 * Advisory only: it never blocks Next or Submit. Matched cases open in a new
 * tab so the half-filled form is not lost.
 */
function DuplicateWarning({ matches, label }: DuplicateWarningProps) {
  const shown = matches.slice(0, VISIBLE_MATCHES);
  const hidden = matches.length - shown.length;
  const warning = tokens.color.callout.warning;

  return (
    <Box role="status" aria-live="polite" aria-atomic="true" sx={{ width: "100%" }}>
      {matches.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: tokens.space[2],
            marginTop: tokens.space[2],
            padding: `${tokens.space[2]} ${tokens.space[3]}`,
            backgroundColor: warning.bg,
            border: `${tokens.borderWidth} solid ${warning.border}`,
            borderRadius: tokens.radius.sm,
          }}
        >
          <WarningAmberOutlinedIcon
            aria-hidden="true"
            sx={{ fontSize: tokens.icon.sm, color: warning.fg, flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ ...caption, fontWeight: tokens.type.denseStrong.weight, color: warning.fg }}
            >
              {matches.length === 1
                ? `A case with this ${label} already exists`
                : `${matches.length} cases with this ${label} already exist`}
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", margin: 0, padding: 0 }}>
              {shown.map((m) => (
                <Box
                  component="li"
                  key={m.file_no}
                  sx={{
                    ...caption,
                    color: tokens.color.text.secondary,
                    marginTop: tokens.space[1],
                    overflowWrap: "anywhere",
                  }}
                >
                  <Box
                    component="a"
                    href={`/app/cases/${encodeURIComponent(m.file_no)}/case`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open case ${m.file_no} in a new tab`}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: tokens.space[1],
                      color: tokens.color.brand.primaryAction,
                      fontWeight: tokens.type.denseStrong.weight,
                      textDecoration: "underline",
                      "&:focus-visible": {
                        outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
                        outlineOffset: tokens.focus.offset,
                      },
                    }}
                  >
                    {m.file_no}
                    <OpenInNewIcon aria-hidden="true" sx={{ fontSize: tokens.icon.sm }} />
                  </Box>
                  {detail(m) && <> · {detail(m)}</>}
                  {m.property_address && (
                    <Box component="span" sx={{ display: "block" }}>
                      {m.property_address}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            {hidden > 0 && (
              <Typography sx={{ ...caption, color: tokens.color.text.secondary, marginTop: tokens.space[1] }}>
                and {hidden} more
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default DuplicateWarning;
